import { TimeReport } from "@entities";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DoneOutlineIcon from "@mui/icons-material/DoneOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { CircularProgress, Stack } from "@mui/material";
import { timeReportRepo } from "@repositories";
import { useCreateHyperTable } from "@shared/frontend/hyper-table";
import { type ActionButton } from "@shared/frontend/hyper-table/inner-components/hyper-table-actions-bar/ActionsBar";
import type { SingleRowActionGetter } from "@shared/frontend/hyper-table/inner-components/hyper-table-menu-actions-column/HyperTableMenuActionsColumn";
import { isEntityFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/FilterMenuItem";
import { type FC, useCallback, useMemo } from "react";
import { useAlert } from "../../../contexts/AlertProvider";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { TableHeaderTitles } from "../table-header/TableHeader";
import { useTimeReportColumns } from "./columns";
import filters from "./filters";
import "./HoursReport.scss";

const HoursReportsTable: FC = () => {
  const { HyperTable } = useCreateHyperTable(TimeReport);
  const { currOrganization } = useOrganization();
  const { setAlertText, setSeverity } = useAlert();

  const buttons = useMemo<ActionButton<TimeReport>[]>(() => {
    return [
      {
        icon: <HighlightOffIcon />,
        onClick: async ({ whereToUpdate }) => {
          await timeReportRepo.updateMany({
            where: whereToUpdate,
            set: {
              isConfirmed: false,
            },
          });
          setAlertText("דיווחי השעות נדחו בהצלחה");
          setSeverity("success");
          HyperTable.context.current?.refetch("remove");
        },
        title: "דחייה",
      },
      {
        icon: <DoneOutlineIcon />,
        onClick: async ({ whereToUpdate }) => {
          await timeReportRepo.updateMany({
            where: whereToUpdate,
            set: {
              isConfirmed: true,
            },
          });
          setAlertText("דיווחי השעות אושרו בהצלחה");
          setSeverity("success");
          HyperTable.context.current?.refetch("remove");
        },
        title: "אישור",
      },
    ];
  }, []);

  if (!currOrganization)
    return (
      <Stack width="100%" height="100%" paddingTop="10vh" alignItems="center">
        <CircularProgress />
      </Stack>
    );

  return (
    <div className="hours-report-table">
      <HyperTable
        tableId="hours-report-table"
        findConfig={{
          where: TimeReport.hyperFilter({
            association: {
              $is: {
                user: {
                  $is: {
                    isDeleted: false,
                  },
                },
              },
            },
          }),
        }}
        onRowClick={(_, row) => { }}
        title={<TableHeaderTitles titles={[{ content: "דיווחי שעות" }]} />}
      >
        <HyperTable.Toolbar>
          {filters.map((filter) =>
            isEntityFilter(filter) ? (
              <HyperTable.EntityFilter key={filter.id} {...(filter as any)} />
            ) : (
              <HyperTable.CustomFilter key={filter.id} {...(filter as any)} />
            ),
          )}
        </HyperTable.Toolbar>
        {useTimeReportColumns().map((column) => (
          <HyperTable.Column key={column.field} {...column} />
        ))}
        <HyperTable.SelectColumn />
        <HyperTable.ActionsBar buttons={buttons} />
      </HyperTable>
    </div>
  );
};

export default HoursReportsTable;
